import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ProfilePictureUpload } from '../components/profile/ProfilePictureUpload';
import { usersApi } from '../utils/api';

export const Account: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    marketingOptIn: user?.account?.settings?.marketingOptIn || false
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setIsUpdating(true);
      const response = await usersApi.updateProfile(formData);
      
      if (response.data.success && response.data.data && response.data.data.user) {
        updateUser(response.data.data.user);
        alert('Profile updated successfully!');
      }
    } catch (error: any) {
      console.error('Profile update failed:', error);
      alert(`Failed to update profile: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleProfilePictureUpdated = (url: string) => {
    console.log('Profile picture updated:', url);
    // The ProfilePictureUpload component already updates the user context
  };

  if (!user) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Account</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Please log in to view your account.
          </p>
          <button className="btn-primary">
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-8">Account Settings</h1>
      
      <div className="max-w-2xl space-y-8">
        {/* Profile Section */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-6">Profile</h2>
          
          {/* Profile Picture Section */}
          <div className="mb-8">
            <h3 className="text-lg font-medium mb-4">Profile Picture</h3>
            <ProfilePictureUpload 
              onProfilePictureUpdated={handleProfilePictureUpdated}
              className="flex justify-center"
            />
          </div>

          {/* Profile Form */}
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                className="input bg-slate-100 dark:bg-slate-700"
              />
              <p className="text-sm text-slate-500 mt-1">Email cannot be changed</p>
            </div>
            <div>
              <label className="form-label">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your name"
                className="input"
              />
            </div>
            <div>
              <label className="form-label">Role</label>
              <select className="input bg-slate-100 dark:bg-slate-700" value={user.role} disabled>
                <option value="buyer">Buyer</option>
                <option value="seller">Seller</option>
                <option value="admin">Admin</option>
              </select>
              <p className="text-sm text-slate-500 mt-1">Role is managed by account administrators</p>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="marketing"
                name="marketingOptIn"
                checked={formData.marketingOptIn}
                onChange={handleInputChange}
                className="mr-2"
              />
              <label htmlFor="marketing" className="text-sm">
                I'd like to receive marketing emails and updates
              </label>
            </div>
            <button 
              type="submit"
              disabled={isUpdating}
              className="btn-primary"
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
        
        {/* Order History */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">Order History</h2>
          <p className="text-slate-600 dark:text-slate-400">
            No orders yet. Start shopping to see your purchase history here.
          </p>
        </div>
        
        {/* Selling History */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">Selling History</h2>
          <p className="text-slate-600 dark:text-slate-400">
            No sales yet. List your first item to start selling.
          </p>
        </div>
        
        
        {/* Account Actions */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">Account Actions</h2>
          <div className="space-y-3">
            <button
              onClick={handleLogout}
              className="btn-secondary"
            >
              Log Out
            </button>
            <button className="btn-destructive">
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
